import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantBranding } from '@/contexts/TenantBrandingContext';
import { useModules } from '@/contexts/ModulesContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import StudioSelector from '@/components/StudioSelector';
import BusinessSwitcher from '@/components/BusinessSwitcher';
import StatusBanner from '@/components/StatusBanner';
import PlatformDunningBanner from '@/components/PlatformDunningBanner';
import AdminNotificationBell from '@/components/AdminNotificationBell';
import AdminAiWidget from '@/components/dashboard/AdminAiWidget';
import GlobalSearch from '@/components/GlobalSearch';
import VersionBadge from '@/components/VersionBadge';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Package,
  Box,
  CreditCard,
  Mail,
  Settings,
  LogOut,
  Activity,
  UserCircle,
  Receipt,
  MessageCircle,
  ShoppingCart,
  Menu,
  X,
  Building2,
  ChevronDown,
  ChevronRight,
  FileText,
  Wallet,
  Home,
  BarChart3,
  Dumbbell,
  Apple,
  ClipboardList,
  Stethoscope,
  ClipboardCheck,
  Smartphone,
  Heart,
  Target,
  Clock,
  Timer,
  AlarmClock,
  CalendarClock,
  Ticket,
  Globe,
  ShieldCheck,
  Database,
  Lock,
  Zap,
  Plug,
  AlertTriangle,
  ShieldOff,
  KanbanSquare,
  Send,
  ChefHat,
  Megaphone,
  Gift,
  History,
  Wrench,
  Smile
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, logoutOtherDevices, hasPermission } = useAuth();
  const { isModuleEnabled } = useModules();
  const { name: brandName, companyName, companyLogo } = useTenantBranding();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingMenuOpen, setBillingMenuOpen] = useState(
    ['/invoices', '/billing', '/billing-audit', '/payments', '/rent-invoices', '/cards-overview'].includes(location.pathname)
  );
  // Iter 329h: Bestätigungs-Dialog für "Alle anderen Geräte abmelden"
  const [showLogoutAllDialog, setShowLogoutAllDialog] = useState(false);
  const [logoutAllBusy, setLogoutAllBusy] = useState(false);

  // 07/2026: Globales WhatsApp-Unread-Badge + Toast bei neuen Kunden-Nachrichten
  const [waUnread, setWaUnread] = useState(0);
  const waPrevUnread = React.useRef(null);
  React.useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    const check = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const count = data.unread_messages || 0;
        if (waPrevUnread.current !== null && count > waPrevUnread.current && location.pathname !== '/whatsapp') {
          toast.info('💬 Neue WhatsApp-Nachricht von einem Kunden', {
            action: { label: 'Öffnen', onClick: () => navigate('/whatsapp') },
            duration: 8000,
          });
        }
        waPrevUnread.current = count;
        setWaUnread(count);
      } catch { /* still */ }
    };
    check();
    const iv = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, location.pathname]);

  // Konfigurierbare Sidebar: team-weite Reihenfolge/Sichtbarkeit + persönliche
  // Overrides des aktuellen Benutzers (vom Backend bereits zusammengeführt).
  const [sidebarConfig, setSidebarConfig] = useState({ order: [], hidden: [], group_order: {} });
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/sidebar-layout`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setSidebarConfig({
            order: data.order || [],
            hidden: data.hidden || [],
            group_order: data.group_order || {},
          });
        }
      } catch { /* still */ }
    };
    load();
    const onUpd = () => load();
    window.addEventListener('sidebar-layout-updated', onUpd);
    return () => { cancelled = true; window.removeEventListener('sidebar-layout-updated', onUpd); };
  }, []);

  const handleLogoutOtherDevices = async () => {
    if (logoutAllBusy) return;
    setLogoutAllBusy(true);
    try {
      const count = await logoutOtherDevices();
      if (count > 0) {
        toast.success(`✓ ${count} andere ${count === 1 ? 'Session' : 'Sessions'} abgemeldet`);
      } else {
        toast(`Keine anderen aktiven Sessions gefunden`);
      }
    } catch (e) {
      toast.error('Konnte andere Sessions nicht abmelden');
    } finally {
      setLogoutAllBusy(false);
      setShowLogoutAllDialog(false);
    }
  };

  // Hauptnavigation (immer sichtbar)
  const allNavigation = [
    { name: 'Super-Admin', href: '/superadmin', icon: ShieldCheck, roles: ['superadmin'], permission: null },
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['admin'], permission: null },
    { name: 'Kunden', href: '/customers', icon: Users, roles: ['admin', 'trainer'], permission: 'can_view_customers' },
    { name: 'Termine', href: '/appointments', icon: Calendar, roles: ['admin', 'trainer'], permission: 'can_view_calendar', module: 'booking' },
    { name: 'Warteliste', href: '/waitlist', icon: ClipboardList, roles: ['admin', 'trainer'], permission: 'can_view_waitlist', module: 'booking' },
    { name: 'Ressourcen', href: '/resources', icon: Box, roles: ['admin', 'trainer', 'mieter'], permission: 'can_view_resources', module: 'resources' },
    { name: 'Kurse', href: '/courses-admin', icon: CalendarClock, roles: ['admin', 'trainer'], permission: 'can_view_calendar', module: 'courses' },
    { name: 'WhatsApp Chat', href: '/whatsapp', icon: MessageCircle, roles: ['admin'], module: 'whatsapp' },
    { name: 'Zahnschema', href: '/dental', icon: Smile, roles: ['admin', 'trainer'], permission: 'can_view_customers', module: 'dental_chart' },
    { name: 'PA-Befund', href: '/dental-perio', icon: Activity, roles: ['admin', 'trainer'], permission: 'can_view_customers', module: 'dental_chart' },
    { name: 'Dental Recall', href: '/dental-recall', icon: CalendarClock, roles: ['admin', 'trainer'], permission: 'can_view_customers', module: 'dental_recall' },
    { name: 'Dental Abrechnung', href: '/dental-billing', icon: Receipt, roles: ['admin', 'trainer'], permission: 'can_view_customers', module: 'dental_billing' },
    { name: 'Voranschläge', href: '/dental-estimates', icon: ClipboardList, roles: ['admin', 'trainer'], permission: 'can_view_customers', module: 'dental_billing' },
    { name: 'Behandlungsplan', href: '/dental-plans', icon: Stethoscope, roles: ['admin', 'trainer'], permission: 'can_view_customers', module: 'dental_billing' },
    { name: 'Werkstatt', href: '/workshop', icon: Wrench, roles: ['admin', 'trainer'], module: 'workshop_orders' },
  ];

  // Training & Ernährung group
  const trainingNavigation = [
    { name: 'Training', href: '/training', icon: Dumbbell, roles: ['admin', 'trainer'], permission: 'can_view_training', module: 'training' },
    { name: 'WOD Pläne', href: '/wod-training', icon: Target, roles: ['admin', 'trainer'], permission: 'can_view_training', module: 'training' },
    { name: 'Ernährung', href: '/nutrition', icon: Apple, roles: ['admin', 'trainer'], permission: 'can_view_nutrition', module: 'nutrition' },
    { name: 'Rezept-Katalog', href: '/admin/recipes', icon: ChefHat, roles: ['admin'], module: 'nutrition' },
    { name: 'Gesundheit', href: '/health-dashboard', icon: Heart, roles: ['admin', 'trainer'], permission: 'can_view_customers' },
    { name: 'Nährwert-Reviews', href: '/product-reviews', icon: ClipboardCheck, roles: ['admin'], module: 'nutrition' },
  ];

  // Finanzen group
  const finanzenNavigation = [
    { name: 'Kunden-Abos', href: '/customer-subscriptions', icon: CreditCard, roles: ['admin'], module: 'subscriptions' },
    { name: 'Verkaufen', href: '/sales', icon: ShoppingCart, roles: ['admin', 'trainer'], permission: 'can_sell_packages' },
    { name: 'Rabattcodes', href: '/discounts', icon: Ticket, roles: ['admin'] },
  ];

  // Marketing & Analyse group
  const marketingNavigation = [
    { name: 'CRM / Leads', href: '/crm', icon: KanbanSquare, roles: ['admin'], module: 'crm' },
    { name: 'Marketing', href: '/campaigns', icon: Mail, roles: ['admin'], module: 'marketing' },
    { name: 'Werbung (Ads)', href: '/admin/ads', icon: Megaphone, roles: ['admin'], module: 'marketing' },
    { name: 'Treue-Rabatt', href: '/admin/loyalty', icon: Gift, roles: ['admin'], module: 'points' },
    { name: 'Prämien', href: '/admin/rewards', icon: Gift, roles: ['admin'], module: 'points' },
    { name: 'Versand-Protokoll', href: '/message-log', icon: Send, roles: ['admin'] },
    { name: 'Kommunikations-Protokoll', href: '/communication-log', icon: Activity, roles: ['admin'] },
    { name: 'Website Analytics', href: '/website-analytics', icon: Globe, roles: ['admin'], module: 'marketing' },
    { name: 'Funnel-Tracking', href: '/funnel-tracking', icon: BarChart3, roles: ['admin'], module: 'marketing' },
    { name: 'Berichte', href: '/reports', icon: BarChart3, roles: ['admin', 'trainer'], permission: 'can_view_reports' },
    { name: 'Bewertungen', href: '/feedback-analytics', icon: BarChart3, roles: ['admin'], module: 'marketing' },
  ];

  // Personal group
  const personalNavigation = [
    { name: 'Mitarbeiter', href: '/personal', icon: Users, roles: ['admin'], module: 'payroll' },
    { name: 'Stundenerfassung', href: '/personal?tab=stunden', icon: Clock, roles: ['admin', 'trainer'], permission: 'can_view_reports', module: 'payroll' },
    { name: 'Lohn & AHV', href: '/personal?tab=lohn', icon: CreditCard, roles: ['admin'], module: 'payroll' },
  ];

  // System group — sauber gruppiert: Betrieb zuerst, dann Technik-/Entwickler-Tools
  const systemNavigation = [
    { name: 'Globale History', href: '/global-history', icon: History, roles: ['admin'] },
    { name: 'Mobile App', href: '/mobile-app', icon: Package, roles: ['admin'] },
    { name: 'Gelöschte Konten', href: '/admin/deleted-accounts', icon: ShieldOff, roles: ['admin'] },
    { name: 'Bestell-Logins', href: '/admin/vendor-credentials', icon: Lock, roles: ['admin', 'trainer'] },
    { type: 'divider', label: 'Technik', roles: ['superadmin'] },
    { name: 'Mobile Monitor', href: '/mobile-health', icon: Smartphone, roles: ['superadmin'] },
    { name: 'Mobile Errors', href: '/mobile-errors', icon: AlertTriangle, roles: ['superadmin'] },
    { name: 'System Health', href: '/system-health', icon: ShieldCheck, roles: ['superadmin'] },
    { name: 'DB Index-Stats', href: '/admin/index-stats', icon: Database, roles: ['superadmin'] },
    { name: 'Audit-Log', href: '/audit-log', icon: ShieldCheck, roles: ['admin'] },
  ];
  
  // Billing/Invoicing submenu items
  const billingNavigation = [
    { name: 'Alle Rechnungen', href: '/invoices', icon: FileText, roles: ['admin', 'superadmin'], permission: 'can_view_finances', module: 'invoicing' },
    { name: 'Offene Abrechnungen', href: '/billing', icon: Receipt, roles: ['admin', 'trainer'], permission: 'can_view_finances', module: 'invoicing' },
    { name: 'Abrechnungs-Kontrolle', href: '/billing-audit', icon: AlertTriangle, roles: ['admin'], module: 'invoicing' },
    { name: 'Zahlungen', href: '/payments', icon: Wallet, roles: ['admin', 'trainer'], permission: 'can_view_finances', module: 'invoicing' },
    { name: 'Miet-Rechnungen', href: '/rent-invoices', icon: Home, roles: ['admin'], module: 'invoicing' },
    { name: 'Hinterlegte Karten', href: '/cards-overview', icon: CreditCard, roles: ['admin'], module: 'invoicing' },
    { name: 'Physio-Abrechnung', href: '/physio-billing', icon: FileText, roles: ['admin', 'trainer'], permission: 'can_view_finances', module: 'invoicing' },
  ];
  
  // Filter navigation based on user role AND permissions
  // Superadmin (Plattform-Betreiber) sieht alle admin-Menüs (Superset).
  const roleMatches = (itemRoles) => {
    if (!itemRoles) return true;
    if (itemRoles.includes(user?.role)) return true;
    if (user?.role === 'superadmin' && itemRoles.includes('admin')) return true;
    return false;
  };

  const navigation = allNavigation.filter(item => {
    // Check role first
    if (!roleMatches(item.roles)) return false;

    // Modul-Gating: nicht gebuchte Module ausblenden (fail-open ohne Abo)
    if (item.module && !isModuleEnabled(item.module)) return false;

    // For trainers, check specific permission
    if (user?.role === 'trainer' && item.permission) {
      return hasPermission(item.permission);
    }
    
    return true;
  });
  
  const filteredBillingNav = billingNavigation.filter(item => {
    if (!roleMatches(item.roles)) return false;
    if (item.module && !isModuleEnabled(item.module)) return false;
    if (user?.role === 'trainer' && item.permission) {
      return hasPermission(item.permission);
    }
    return true;
  });

  const filterNavGroup = (items) => items.filter(item => {
    if (!roleMatches(item.roles)) return false;
    if (item.module && !isModuleEnabled(item.module)) return false;
    if (user?.role === 'trainer' && item.permission) return hasPermission(item.permission);
    return true;
  });

  const filteredTrainingNav = filterNavGroup(trainingNavigation);
  const filteredFinanzenNav = filterNavGroup(finanzenNavigation);
  const filteredMarketingNav = filterNavGroup(marketingNavigation);
  const filteredSystemNav = filterNavGroup(systemNavigation);
  const filteredPersonalNav = filterNavGroup(personalNavigation);

  // Accordion: only one group open at a time
  const getInitialGroup = () => {
    if (filteredTrainingNav.some(item => location.pathname === item.href)) return 'training';
    if (filteredFinanzenNav.some(item => location.pathname === item.href) ||
        ['/invoices', '/billing', '/billing-audit', '/payments', '/rent-invoices', '/cards-overview'].includes(location.pathname)) return 'finanzen';
    if (filteredMarketingNav.some(item => location.pathname === item.href)) return 'marketing';
    if (location.pathname === '/personal') return 'personal';
    if (filteredSystemNav.some(item => location.pathname === item.href)) return 'system';
    return null;
  };
  const [openGroup, setOpenGroup] = useState(getInitialGroup);

  const toggleGroup = (group) => {
    setOpenGroup(prev => prev === group ? null : group);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };
  
  const isBillingActive = ['/invoices', '/billing', '/billing-audit', '/payments', '/rent-invoices', '/cards-overview'].includes(location.pathname);
  const isTrainingActive = filteredTrainingNav.some(item => location.pathname === item.href);
  const isFinanzenActive = filteredFinanzenNav.some(item => location.pathname === item.href) || isBillingActive;
  const isMarketingActive = filteredMarketingNav.some(item => location.pathname === item.href);
  const isPersonalActive = location.pathname === '/personal';
  const isSystemActive = filteredSystemNav.some(item => location.pathname === item.href);

  // ── Konfigurierbare Sidebar: Helfer für Reihenfolge (CSS order) & Sichtbarkeit ──
  const DEFAULT_TOP_ORDER = [
    '/superadmin', '/', '/customers', '/appointments', '/waitlist', '/resources',
    '/courses-admin', '/whatsapp', '/dental', '/dental-perio', '/dental-recall', '/dental-billing', '/dental-estimates', '/dental-plans', '/workshop',
    'group:training', 'group:finanzen', 'group:marketing', 'group:personal', 'group:system',
    '/mein-abo', '/meine-rechnungen', '/settings',
  ];
  const sbHidden = new Set(sidebarConfig.hidden || []);
  const sbOrder = sidebarConfig.order || [];
  const sbGroupOrder = sidebarConfig.group_order || {};
  const isHidden = (key) => sbHidden.has(key);
  // Effektive Reihenfolge: gespeicherte Config, ergänzt um neu hinzugekommene
  // Standard-Einträge direkt hinter ihrem Standard-Vorgänger (statt ans Ende).
  const effectiveOrder = (() => {
    if (!sbOrder.length) return DEFAULT_TOP_ORDER;
    const result = [...sbOrder];
    DEFAULT_TOP_ORDER.forEach((key, i) => {
      if (result.includes(key)) return;
      let insertAt = result.length;
      for (let j = i - 1; j >= 0; j--) {
        const idx = result.indexOf(DEFAULT_TOP_ORDER[j]);
        if (idx >= 0) { insertAt = idx + 1; break; }
      }
      result.splice(insertAt, 0, key);
    });
    return result;
  })();
  const navOrder = (key) => {
    if (key === '/superadmin') return -1;
    const i = effectiveOrder.indexOf(key);
    return i >= 0 ? i : 500;
  };
  const childOrder = (groupId, href) => {
    const arr = sbGroupOrder[groupId] || [];
    const i = arr.indexOf(href);
    return i >= 0 ? i : 100;
  };
  
  const getPageTitle = () => {
    const allNavs = [navigation, filteredBillingNav, filteredTrainingNav, filteredFinanzenNav, filteredMarketingNav, filteredSystemNav];
    for (const nav of allNavs) {
      const found = nav.find(n => n.href === location.pathname);
      if (found) return found.name;
    }
    if (location.pathname.startsWith('/settings')) return 'Einstellungen';
    if (location.pathname.startsWith('/staff')) return 'Personal';
    if (location.pathname === '/timetracking') return 'Stundenerfassung';
    return 'Dashboard';
  };

  return (
    <div className="min-h-screen flex" data-testid="dashboard-layout">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile, shown on desktop */}
      <aside className={`
        fixed h-screen overflow-y-auto custom-scrollbar sidebar z-50
        transform transition-transform duration-300 ease-in-out
        w-64
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="p-4 lg:p-6">
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <div className="flex items-center gap-2" data-testid="sidebar-brand">
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={companyName || brandName}
                  className="h-8 w-8 lg:h-9 lg:w-9 rounded-md object-contain shrink-0"
                  data-testid="sidebar-brand-logo"
                />
              ) : (
                <Activity className="text-teal-600" size={28} />
              )}
              <h1 className="text-xl lg:text-2xl font-bold text-gray-800 truncate" data-testid="sidebar-brand-name">
                {companyLogo ? (companyName || brandName) : `${brandName} Hub`}
              </h1>
            </div>
            {/* Close button for mobile */}
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col gap-1">
            {/* Hauptnavigation */}
            {navigation.map((item) => {
              if (isHidden(item.href)) return null;
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleNavClick}
                  style={{ order: navOrder(item.href) }}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon size={20} />
                  <span>{item.name}</span>
                  {item.name === 'WhatsApp Chat' && waUnread > 0 && (
                    <span
                      data-testid="whatsapp-unread-badge"
                      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold"
                    >
                      {waUnread > 99 ? '99+' : waUnread}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* Training & Ernährung */}
            {!isHidden('group:training') && filteredTrainingNav.length > 0 && (
              <div className="rounded-lg overflow-hidden" style={{ order: navOrder('group:training') }}>
                <button
                  onClick={() => toggleGroup('training')}
                  className={`sidebar-link w-full justify-between font-semibold transition-colors ${
                    openGroup === 'training' 
                      ? 'bg-teal-50 text-teal-700' 
                      : isTrainingActive ? 'bg-teal-50/50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="nav-training-folder"
                >
                  <div className="flex items-center gap-3">
                    <Dumbbell size={20} />
                    <span>Training & Ernährung</span>
                  </div>
                  {openGroup === 'training' ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {openGroup === 'training' && (
                  <div className="bg-gray-100 rounded-b-lg py-1 ml-2 border-l-[3px] border-teal-400 pl-3 flex flex-col gap-0.5">
                    {filteredTrainingNav.map((subItem) => {
                      if (isHidden(subItem.href)) return null;
                      const SubIcon = subItem.icon;
                      const isSubActive = location.pathname === subItem.href;
                      return (
                        <Link key={subItem.name} to={subItem.href} onClick={handleNavClick}
                          style={{ order: childOrder('training', subItem.href) }}
                          className={`sidebar-link text-sm py-1.5 ${isSubActive ? 'active' : ''}`}
                          data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <SubIcon size={16} />
                          <span>{subItem.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Finanzen */}
            {!isHidden('group:finanzen') && (filteredFinanzenNav.length > 0 || filteredBillingNav.length > 0) && (
              <div className="rounded-lg overflow-hidden" style={{ order: navOrder('group:finanzen') }}>
                <button
                  onClick={() => toggleGroup('finanzen')}
                  className={`sidebar-link w-full justify-between font-semibold transition-colors ${
                    openGroup === 'finanzen'
                      ? 'bg-teal-50 text-teal-700'
                      : isFinanzenActive ? 'bg-teal-50/50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="nav-finanzen-folder"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard size={20} />
                    <span>Finanzen</span>
                  </div>
                  {openGroup === 'finanzen' ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {openGroup === 'finanzen' && (
                  <div className="bg-gray-100 rounded-b-lg py-1 ml-2 border-l-[3px] border-teal-400 pl-3 flex flex-col gap-0.5">
                    {filteredFinanzenNav.map((subItem) => {
                      if (isHidden(subItem.href)) return null;
                      const SubIcon = subItem.icon;
                      const isSubActive = location.pathname === subItem.href;
                      return (
                        <Link key={subItem.name} to={subItem.href} onClick={handleNavClick}
                          style={{ order: childOrder('finanzen', subItem.href) }}
                          className={`sidebar-link text-sm py-2 ${isSubActive ? 'active' : ''}`}
                          data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <SubIcon size={16} />
                          <span>{subItem.name}</span>
                        </Link>
                      );
                    })}
                    {filteredBillingNav.length > 0 && (
                      <>
                        <button
                          onClick={() => setBillingMenuOpen(!billingMenuOpen)}
                          style={{ order: 99 }}
                          className={`sidebar-link text-sm py-2 w-full justify-between ${isBillingActive ? 'bg-teal-50 text-teal-700' : ''}`}
                          data-testid="nav-rechnungen-folder"
                        >
                          <div className="flex items-center gap-2">
                            <FileText size={16} />
                            <span>Rechnungen</span>
                          </div>
                          {billingMenuOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        {billingMenuOpen && (
                          <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l-2 border-gray-100 pl-2" style={{ order: 100 }}>
                            {filteredBillingNav.map((subItem) => {
                              if (isHidden(subItem.href)) return null;
                              const SubIcon = subItem.icon;
                              const isSubActive = location.pathname === subItem.href;
                              return (
                                <Link key={subItem.name} to={subItem.href} onClick={handleNavClick}
                                  style={{ order: childOrder('finanzen', subItem.href) }}
                                  className={`sidebar-link text-xs py-1.5 ${isSubActive ? 'active' : ''}`}
                                  data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <SubIcon size={14} />
                                  <span>{subItem.name}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Marketing & Analyse */}
            {!isHidden('group:marketing') && filteredMarketingNav.length > 0 && (
              <div className="rounded-lg overflow-hidden" style={{ order: navOrder('group:marketing') }}>
                <button
                  onClick={() => toggleGroup('marketing')}
                  className={`sidebar-link w-full justify-between font-semibold transition-colors ${
                    openGroup === 'marketing'
                      ? 'bg-teal-50 text-teal-700'
                      : isMarketingActive ? 'bg-teal-50/50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="nav-marketing-folder"
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 size={20} />
                    <span>Marketing & Analyse</span>
                  </div>
                  {openGroup === 'marketing' ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {openGroup === 'marketing' && (
                  <div className="bg-gray-100 rounded-b-lg py-1 ml-2 border-l-[3px] border-teal-400 pl-3 flex flex-col gap-0.5">
                    {filteredMarketingNav.map((subItem) => {
                      if (isHidden(subItem.href)) return null;
                      const SubIcon = subItem.icon;
                      const isSubActive = location.pathname === subItem.href;
                      return (
                        <Link key={subItem.name} to={subItem.href} onClick={handleNavClick}
                          style={{ order: childOrder('marketing', subItem.href) }}
                          className={`sidebar-link text-sm py-1.5 ${isSubActive ? 'active' : ''}`}
                          data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <SubIcon size={16} />
                          <span>{subItem.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Personal */}
            {!isHidden('group:personal') && filteredPersonalNav.length > 0 && (
              <div className="rounded-lg overflow-hidden" style={{ order: navOrder('group:personal') }}>
                <button
                  onClick={() => toggleGroup('personal')}
                  className={`sidebar-link w-full justify-between font-semibold transition-colors ${
                    openGroup === 'personal'
                      ? 'bg-teal-50 text-teal-700'
                      : isPersonalActive ? 'bg-teal-50/50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="nav-personal-folder"
                >
                  <div className="flex items-center gap-3">
                    <Users size={20} />
                    <span>Personal</span>
                  </div>
                  {openGroup === 'personal' ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {openGroup === 'personal' && (
                  <div className="bg-gray-100 rounded-b-lg py-1 ml-2 border-l-[3px] border-teal-400 pl-3 flex flex-col gap-0.5">
                    {filteredPersonalNav.map((subItem) => {
                      if (isHidden(subItem.href)) return null;
                      const SubIcon = subItem.icon;
                      const isSubActive = location.pathname + location.search === subItem.href
                        || (subItem.href === '/personal' && location.pathname === '/personal' && !location.search);
                      return (
                        <Link key={subItem.name} to={subItem.href} onClick={handleNavClick}
                          style={{ order: childOrder('personal', subItem.href) }}
                          className={`sidebar-link text-sm py-1.5 ${isSubActive ? 'active' : ''}`}
                          data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <SubIcon size={16} />
                          <span>{subItem.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* System */}
            {!isHidden('group:system') && filteredSystemNav.length > 0 && (
              <div className="rounded-lg overflow-hidden" style={{ order: navOrder('group:system') }}>
                <button
                  onClick={() => toggleGroup('system')}
                  className={`sidebar-link w-full justify-between font-semibold transition-colors ${
                    openGroup === 'system'
                      ? 'bg-teal-50 text-teal-700'
                      : isSystemActive ? 'bg-teal-50/50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="nav-system-folder"
                >
                  <div className="flex items-center gap-3">
                    <ShieldCheck size={20} />
                    <span>System</span>
                  </div>
                  {openGroup === 'system' ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {openGroup === 'system' && (
                  <div className="bg-gray-100 rounded-b-lg py-1 ml-2 border-l-[3px] border-teal-400 pl-3 flex flex-col gap-0.5">
                    {filteredSystemNav.map((subItem) => {
                      if (subItem.type === 'divider') {
                        return (
                          <div key={`divider-${subItem.label}`} style={{ order: 100 }} className="flex items-center gap-2 px-1 pt-2 pb-1" data-testid={`nav-system-divider-${subItem.label.toLowerCase()}`}>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{subItem.label}</span>
                            <div className="flex-1 border-t border-gray-300"></div>
                          </div>
                        );
                      }
                      if (isHidden(subItem.href)) return null;
                      const SubIcon = subItem.icon;
                      const isSubActive = location.pathname === subItem.href;
                      return (
                        <Link key={subItem.name} to={subItem.href} onClick={handleNavClick}
                          style={{ order: childOrder('system', subItem.href) }}
                          className={`sidebar-link text-sm py-1.5 ${isSubActive ? 'active' : ''}`}
                          data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <SubIcon size={16} />
                          <span>{subItem.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Mein Abo - single link */}
            {user?.role === 'admin' && !isHidden('/mein-abo') && (
              <Link
                to="/mein-abo"
                onClick={handleNavClick}
                style={{ order: navOrder('/mein-abo') }}
                className={`sidebar-link ${location.pathname === '/mein-abo' ? 'active' : ''}`}
                data-testid="nav-mein-abo"
              >
                <CreditCard size={20} />
                <span>Mein Abo</span>
              </Link>
            )}

            {/* Meine Plattform-Rechnungen */}
            {user?.role === 'admin' && !isHidden('/meine-rechnungen') && (
              <Link
                to="/meine-rechnungen"
                onClick={handleNavClick}
                style={{ order: navOrder('/meine-rechnungen') }}
                className={`sidebar-link ${location.pathname === '/meine-rechnungen' ? 'active' : ''}`}
                data-testid="nav-meine-rechnungen"
              >
                <Receipt size={20} />
                <span>Meine Rechnungen</span>
              </Link>
            )}

            {/* Einstellungen - single link */}
            {(user?.role === 'admin' || hasPermission('can_view_settings')) && !isHidden('/settings') && (
              <Link
                to="/settings"
                onClick={handleNavClick}
                style={{ order: navOrder('/settings') }}
                className={`sidebar-link ${location.pathname === '/settings' ? 'active' : ''}`}
                data-testid="nav-einstellungen"
              >
                <Settings size={20} />
                <span>Einstellungen</span>
              </Link>
            )}
          </nav>
          
          {/* Mobile logout button in sidebar */}
          <div className="mt-6 pt-6 border-t lg:hidden">
            <div className="px-3 py-2 text-sm text-gray-600 mb-2">
              {user?.first_name} {user?.last_name}
              <span className="block text-xs text-gray-400">{user?.role}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="w-full"
            >
              <LogOut size={16} className="mr-2" />
              Abmelden
            </Button>
          </div>

          {/* Version indicator (subtle, both mobile & desktop) */}
          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-center">
            <VersionBadge />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 w-full">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 py-3 lg:py-4">
            <div className="flex items-center gap-3">
              {/* Hamburger menu button for mobile */}
              <button 
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu size={24} />
              </button>
              <div>
                <h2 className="text-lg lg:text-xl font-semibold text-gray-800" data-testid="page-title">
                  {getPageTitle()}
                </h2>
              </div>
            </div>
            
            {/* Center: Suche + Studio Selector */}
            <div className="hidden md:flex items-center gap-3">
              <GlobalSearch />
              <BusinessSwitcher />
              <StudioSelector />
            </div>
            
            {/* User info - Hidden on mobile, shown on desktop */}
            <div className="hidden lg:flex items-center gap-4">
              <AdminNotificationBell />
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800" data-testid="user-name">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-500" data-testid="user-role">{user?.role}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut size={16} className="mr-2" />
                Abmelden
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2"
                    aria-label="Konto-Menü"
                    data-testid="account-menu-trigger"
                  >
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="account-menu-content">
                  <DropdownMenuLabel className="text-xs text-gray-500 font-normal">
                    Sicherheit
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setShowLogoutAllDialog(true); }}
                    className="cursor-pointer"
                    data-testid="logout-other-devices-trigger"
                  >
                    <LogOut size={14} className="mr-2 text-rose-600" />
                    <div className="flex flex-col">
                      <span className="text-sm">Alle anderen Geräte abmelden</span>
                      <span className="text-xs text-gray-500">Notfall-Knopf bei Handy-Verlust</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); handleLogout(); }}
                    className="cursor-pointer"
                    data-testid="logout-via-menu"
                  >
                    <LogOut size={14} className="mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* Mobile: Studio + User initials */}
            <div className="lg:hidden flex items-center gap-2">
              <AdminNotificationBell />
              <div className="md:hidden">
                <StudioSelector />
              </div>
              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-sm font-medium">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
            </div>
          </div>
        </header>

        {/* Status Banner for service issues */}
        <StatusBanner variant="admin" />

        {/* Mahnbanner bei offenen/überfälligen Plattform-Rechnungen */}
        {user?.role === 'admin' && <PlatformDunningBanner />}

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* 10/2026: KI-Admin-Assistent auf ALLEN Admin-Seiten sichtbar (nicht nur Dashboard) */}
      {user?.role === 'admin' && <AdminAiWidget />}

      {/* Iter 329h: Logout-Other-Devices Bestätigungsdialog */}
      <AlertDialog open={showLogoutAllDialog} onOpenChange={setShowLogoutAllDialog}>
        <AlertDialogContent data-testid="logout-other-devices-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Alle anderen Geräte abmelden?</AlertDialogTitle>
            <AlertDialogDescription>
              Alle Sessions auf <strong>anderen Geräten</strong> (Mobile-App, andere Browser, Tablet) werden sofort beendet. 
              <br /><br />
              Dieses Gerät bleibt eingeloggt.
              <br /><br />
              Verwende das, wenn du dein Handy verloren hast oder einen verdächtigen Login bemerkst.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-other-devices-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogoutOtherDevices}
              disabled={logoutAllBusy}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="logout-other-devices-confirm"
            >
              {logoutAllBusy ? 'Wird abgemeldet…' : 'Ja, alle anderen abmelden'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DashboardLayout;
